import React, { Component } from 'react';
import { connect } from 'react-redux';
import AnalyticsModel from '../models';
import AnalyticsHeader from './components/header';
import AnalyticsSummary from './components/summary/index';
import CommentsChart from './components/charts/comments';
import LikesChart from './components/charts/likes';
import PageViewsChart from './components/charts/page-views';
import RegistrationsChart from './components/charts/registrations';
import { Element } from 'react-scroll';
import { fetchAnalyticsOverview } from './state';
import { DAILY } from '../models/resolution';
import { ALL } from '../../../models/device-type';
import { TAnalyticsParams, TChart, TResolution } from '../types';
import { FetchError } from '@host-panel/analytics/components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { State } from 'state';

dayjs.extend(utc);

type BaseProps = {
  spotId: string;
  ssoEnabled: boolean;
};

interface InjectedProps {
  isLoading: boolean;
  analytics: AnalyticsModel;
  onFetchAnalyticsOverview: (params: TAnalyticsParams) => void;
}

type Props = BaseProps & InjectedProps;

type InitialState = TAnalyticsParams;

const mapStateToProps = (state: State) => {
  const {
    analytics: {
      overview: { isLoading, hasError, analytics }
    }
  } = state;
  return { isLoading, hasError, analytics };
};

const mapDispatchToProps = (dispatch: any) => ({
  onFetchAnalyticsOverview: ({
    spotId,
    device,
    resolution,
    fromDay,
    toDay,
    type
  }: TAnalyticsParams) => {
    dispatch(fetchAnalyticsOverview({ spotId, device, resolution, fromDay, toDay, type }));
  }
});

const getInitialState = (props: Props): InitialState => {
  let fromDay = dayjs().subtract(7, 'd').utc().unix();
  let toDay = dayjs().utc().unix();

  return {
    device: ALL,
    resolution: DAILY,
    fromDay,
    toDay,
    type: 'all'
  };
};

class Overview extends Component<Props, InitialState> {
  state: InitialState = getInitialState(this.props);

  componentDidMount() {
    const { spotId, onFetchAnalyticsOverview } = this.props;
    const { device, resolution, fromDay, toDay } = this.state;

    onFetchAnalyticsOverview({ spotId, device, resolution, fromDay, toDay });
  }

  componentDidUpdate(prevProps: Props, prevState: InitialState) {
    const { isLoading } = this.props;
    if (prevState.type && prevProps.isLoading && !isLoading) {
      this.setState({ type: 'all' });
    }
  }

  onDateChange(params: TAnalyticsParams) {
    const { onFetchAnalyticsOverview } = this.props;
    const { device } = this.state;
    const { spotId, fromDay, toDay, resolution } = params;

    this.setState({ fromDay, toDay });
    onFetchAnalyticsOverview({ spotId, device, resolution, fromDay, toDay });
  }

  onDeviceChange(params: TAnalyticsParams) {
    const { onFetchAnalyticsOverview } = this.props;
    const { fromDay, toDay } = this.state;
    const { spotId, device, resolution } = params;

    this.setState({ device });
    onFetchAnalyticsOverview({ spotId, device, resolution, fromDay, toDay });
  }

  onResolutionChange(resolution: TResolution, type: TChart) {
    const { spotId, onFetchAnalyticsOverview } = this.props;
    const { fromDay, toDay, device } = this.state;

    this.setState({ resolution, type });
    onFetchAnalyticsOverview({ spotId, device, resolution, fromDay, toDay, type });
  }

  render() {
    const { spotId, isLoading, hasError, analytics, ssoEnabled } = this.props;
    const { type } = this.state;
    const summary = analytics.summary;
    const isSso = ssoEnabled;
    const graphStyle = { borderBottom: '#e5e6e6 1px solid', height: '350px' };

    return (
      <div className="analytics">
        {!hasError && (
          <>
            <AnalyticsHeader
              loading={isLoading}
              dateChange={this.onDateChange.bind(this)}
              deviceChange={this.onDeviceChange.bind(this)}
              textHeader={'Overview'}
              analytics={analytics}
              spotId={spotId}
            />

            {isLoading && type === 'all' ? null : (
              <div>
                <AnalyticsSummary summary={summary} isSso={isSso} />

                <Element name="pageViews" style={graphStyle}>
                  <PageViewsChart
                    resolutionChange={this.onResolutionChange.bind(this)}
                    analytics={analytics}
                    spotId={spotId}
                    loading={type === 'page_views'}
                  />
                </Element>

                <Element name="comments" style={graphStyle}>
                  <CommentsChart
                    resolutionChange={this.onResolutionChange.bind(this)}
                    analytics={analytics}
                    spotId={spotId}
                    loading={type === 'comments'}
                  />
                </Element>
                <Element name="ranks" style={graphStyle}>
                  <LikesChart
                    analytics={analytics}
                    resolutionChange={this.onResolutionChange.bind(this)}
                    spotId={spotId}
                    loading={type === 'likes'}
                  />
                </Element>
                {!isSso && (
                  <Element name="registrations">
                    <RegistrationsChart
                      analytics={analytics}
                      resolutionChange={this.onResolutionChange.bind(this)}
                      spotId={spotId}
                      loading={type === 'registrations'}
                    />
                  </Element>
                )}
              </div>
            )}
          </>
        )}

        {hasError && <FetchError />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
